import { FC, useEffect } from 'react';

import { useProductsPublicSendOfferUsagesFeatures } from '@eneco/dc/hooks';
import { FlowRadioGroup, getRadioGroupNameProps } from '@eneco/flows/components';
import { requiredRadioChoice } from '@eneco/flows/helpers';
import { RadioTile } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { MovingStepRendering } from '@sitecore/types/MovingStep';
import * as yup from 'yup';

import { StepShell } from '../../components/StepShell/StepShell';
import { useFlowStep } from '../../hooks';

interface FormFields {
  isMoving: string;
}

export const MovingStep: FC = () => {
  const { send } = useProductsPublicSendOfferUsagesFeatures();

  const { MovingStep: { fields: textualData } = {} } = usePlaceholderContent<{ MovingStep: MovingStepRendering }>();

  const {
    context: {
      usageGas,
      usageElectricityHigh,
      usageElectricityLow,
      solarPanelsOutput,
      postalCode,
      houseNumber,
      houseNumberSuffix,
    },
    form: {
      handleSubmit,
      control,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<FormFields>({
    buildSchema: () =>
      yup.object({
        // Radios submit string values ("true" / "false"); boolean().required() can treat "" as false.
        isMoving: requiredRadioChoice(textualData?.choice?.isMovingRadio?.value?.requiredMessage),
      }),
    defaultValues: context => ({ isMoving: context.isMoving?.toString() }),
    onSubmitSuccess: formData => ({
      isMoving: formData.isMoving === 'true',
      ci: formData.isMoving === 'true',
    }),
  });

  useEffect(() => {
    send({
      offerUsages: {
        gas: usageGas,
        electricityNormal: usageElectricityHigh,
        electricityLow: usageElectricityLow,
        electricityRedeliveryNormal: solarPanelsOutput,
      },
      address: {
        postalCode: postalCode,
        houseNumber: houseNumber,
        houseNumberSuffix: houseNumberSuffix,
      },
    });
  }, []);

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      <FlowRadioGroup
        control={control}
        name="isMoving"
        {...getRadioGroupNameProps(textualData?.choice?.isMovingRadio?.value?.label)}
        hint={textualData?.choice?.isMovingRadio?.value?.hint || undefined}
        direction="column"
        error={errors['isMoving']?.message}>
        {textualData?.choice?.isMovingRadio?.value?.options?.map(option => (
          <RadioTile key={option.name} value={option.name} subtitle={option.description || undefined}>
            {option.label}
          </RadioTile>
        ))}
      </FlowRadioGroup>
    </StepShell>
  );
};
